import {
  type FC, type ReactNode, useCallback, useEffect, useState,
} from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import {
  ButtonComponent, ButtonSize,
} from '~/_shared/baseComponents/buttons';
import { TextInputComponent } from '~/_shared/baseComponents/inputs';
import { SliderWithValueLabelsComponent } from '~/_shared/components/slider/sliderWithValueLabels/sliderWithValueLabels.component';
import {
  TextInputWithGeolocationComponent, type TextInputWithGeolocationProps,
} from '~/_shared/components/textInputWithGeolocation/textInputWithGeolocation.compontent';
import { useTranslation } from '~/_shared/utils/hooks';
import { clamp } from '~/_shared/utils/number/number.helpers';
import { isNullsy } from '~/_shared/utils/typeGuards';
import { UnitDropdownComponent } from '~/proximity/panel/form/formFields/unitDropdown.component';
import {
  PLACES_SEARCH_DISTANCE_MAX, PLACES_SEARCH_DISTANCE_MIN, type PlaceSearchListingItem,
} from '~/sidebar/sidebarApps/mapTools/placesTool/placesTool.container';
import {
  buttonStyle, distanceStyle, exampleStyle, formStyle, hideStyle, labelStyle, placesToolAlertStyle, placesToolContainerStyle, settingStyle,
  sliderStyle, unitStyle,
} from '~/sidebar/sidebarApps/mapTools/placesTool/placesTool.styles';
import type { PlacesDistance } from '~/store/mapSettings/places/mapSettingsPlaces.state';
import { useMapSettingsSettingsUnitSystemSelector } from '~/store/mapSettings/settings/mapSettingsSettings.selectors';
import { Trans } from '~/translations/Trans';
import { MapToolWrapperComponent } from '../../mapToolWrapper/mapToolWrapper.component';
import { PlacesToolListingContainer } from './placesToolListing.container';

const DISTANCE_DECIMAL_PLACES = 2;
const SLIDER_MIN = 1;

export type PlacesSearchProps = Readonly<{
  searchField: string;
  distance: PlacesDistance;
}>;

type PlacesToolComponentProps = Readonly<{
  error: ReactNode;
  geolocationInputProps: TextInputWithGeolocationProps;
  isFormLoading: boolean;
  isAddressDataValid: boolean;
  isLegendMode: boolean;
  searches: ReadonlyArray<PlaceSearchListingItem>;
  searchFieldInitialState?: string;

  createSearch: (searchProps: PlacesSearchProps) => Promise<void>;
}>;

export const PlacesToolComponent: FC<PlacesToolComponentProps> = ({
  error,
  createSearch,
  isLegendMode,
  isFormLoading,
  isAddressDataValid,
  geolocationInputProps,
  searches,
  searchFieldInitialState,
}: PlacesToolComponentProps) => {
  const [t] = useTranslation();
  const defaultUnitSystem = useMapSettingsSettingsUnitSystemSelector();

  const [searchField, setSearchField] = useState(searchFieldInitialState ?? '');
  const [searchDistance, setSearchDistance] = useState<string | null>('3');
  const [distanceUnit, setDistanceUnit] = useState(defaultUnitSystem);

  const handleSearch = useCallback(() => {
    if (!searchField || isNullsy(searchDistance)) {
      return;
    }

    createSearch({
      distance: { value: parseFloat(searchDistance), unit: distanceUnit },
      searchField,
    }).then(() => {
      setSearchField('');
    });
  }, [createSearch, distanceUnit, searchDistance, searchField]);

  const setDistanceFromText = useCallback((distanceText: string) =>
    setSearchDistance(() => (
      distanceText
        .replace(/[^0-9.]/g, '')
        .replace(/(\.\d*)\..*/, '$1') // only one dot
        .replace(new RegExp(`(\\.\\d{${DISTANCE_DECIMAL_PLACES}})\\d+`), '$1')
    )), []);

  const handleDistanceChange = useCallback(([distance]: [number]) =>
    setSearchDistance('' + distance), []);

  const setDistanceClamped = useCallback(() => {
    const value = parseFloat(searchDistance ?? '');
    if (!isNaN(value)) {
      setSearchDistance('' + clamp(value, { min: PLACES_SEARCH_DISTANCE_MIN, max: PLACES_SEARCH_DISTANCE_MAX }));
    }
  }, [searchDistance]);

  const searchDistanceFloat = parseFloat(searchDistance ?? '') || 0;
  const isFormValid = isAddressDataValid && !isFormLoading
    && searchField.trim().length > 0 && searchDistanceFloat >= PLACES_SEARCH_DISTANCE_MIN;

  useEffect(() => {
    if (searchFieldInitialState && searchFieldInitialState !== '') {
      setSearchField(searchFieldInitialState);
    }
  }, [searchFieldInitialState]);

  return (
    <MapToolWrapperComponent>
      <div css={placesToolContainerStyle}>

        {!isLegendMode && (
          <div css={formStyle}>
            <div css={settingStyle}>
              <span css={labelStyle}>
                <Trans
                  components={{ example: <span css={exampleStyle} /> }}
                  tOptions={{
                    placeholder: t('placesTool.searchField.example'),
                  }}
                  i18nKey="placesTool.step1.label"
                />
              </span>
              <TextInputComponent
                onChange={setSearchField}
                placeholder={t('placesTool.step1.placeholder')}
                value={searchField}
              />
            </div>

            <div css={settingStyle}>
              <span css={labelStyle}>
                <Trans
                  components={{ example: <span css={exampleStyle} /> }}
                  i18nKey="placesTool.step2.label"
                />
              </span>
              <TextInputWithGeolocationComponent
                placeholder={t('placesTool.step2.placeholder')}
                {...geolocationInputProps}
              />
            </div>

            <div css={settingStyle}>
              <span css={labelStyle}>
                {t('placesTool.step3.label')}
              </span>

              <span css={distanceStyle}>
                <SliderWithValueLabelsComponent
                  css={sliderStyle}
                  handleLabelStyleCommon={hideStyle}
                  max={PLACES_SEARCH_DISTANCE_MAX}
                  min={SLIDER_MIN}
                  onChange={handleDistanceChange}
                  value={[searchDistanceFloat ?? 0]}
                  valueSuffix={` ${t(distanceUnit)}`}
                />
                <UnitDropdownComponent
                  hideIcon
                  inputStyle={unitStyle}
                  max={PLACES_SEARCH_DISTANCE_MAX}
                  min={PLACES_SEARCH_DISTANCE_MIN}
                  onBlur={setDistanceClamped}
                  onChange={setDistanceFromText}
                  onUnitChange={setDistanceUnit}
                  unit={distanceUnit}
                  value={searchDistance?.toString() ?? ''}
                />
              </span>
            </div>

            <ButtonComponent
              css={buttonStyle}
              onClick={isFormValid ? handleSearch : undefined}
              size={ButtonSize.Large}
              text={t('Search')}
              isDisabled={!isFormValid}
            />

            {error && (
              <AlertComponent
                size="small"
                type="danger"
                css={placesToolAlertStyle}
              >
                {error}
              </AlertComponent>
            )}

          </div>
        )}

        <PlacesToolListingContainer
          searches={searches}
        />

      </div>
    </MapToolWrapperComponent>
  );
};
